import * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn } from '@/lib/utils'
import { gen_color } from '@/utils/colors'

export interface NameAvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  name: string
  src?: string
  alt?: string
}

const getInitials = (name: string): string => {
  if (!name) return '?'
  
  // Arabic characters are in the range \u0600-\u06FF
  const isArabic = /[\u0600-\u06FF]/.test(name)
  
  // Split by spaces and get first letters
  const parts = name.split(' ').filter(part => part.length > 0)
  
  if (parts.length === 0) return '?'
  
  // Take up to two parts (first and last name)
  const relevantParts = parts.length > 1 
    ? [parts[0], parts[parts.length - 1]] 
    : [parts[0]]
    
  // Get first character of each part
  const initials = relevantParts
    .map(part => part.charAt(0))
    .join('')
    
  // For Arabic, we need to ensure characters are not connected
  return isArabic 
    ? initials.split('').join(' ') // Add space between Arabic characters
    : initials.toUpperCase().substring(0, 2) // For non-Arabic, limit to 2 chars and uppercase
}

export const NameAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  NameAvatarProps
>(({ className, name, src, alt, ...props }, ref) => {
  const { backgroundColor, textColorClass } = gen_color(name || 'User')
  const initials = getInitials(name || 'User')

  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
        className
      )}
      {...props}
    >
      {src && (
        <AvatarPrimitive.Image
          src={src}
          alt={alt || name || ''}
          className="aspect-square h-full w-full"
        />
      )}
      <AvatarPrimitive.Fallback
        className={cn(
          'flex h-full w-full items-center justify-center rounded-full',
          textColorClass
        )}
        style={{ backgroundColor }}
      >
        {initials}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
})

NameAvatar.displayName = 'NameAvatar'